/**
 * VolleyballStatsCard
 *
 * Gamified volleyball career statistics card.
 * Shows player level/XP, core stat spotlights, career highs with trophy
 * styling, an enhanced hitting-% donut, per-set progress bars, and a
 * recent-matches timeline.
 *
 * Props:
 *   stats  — object returned by teamService.getPlayerCareerStats()
 *   user   — { name, username, ... } from AuthContext
 */
import React, { useMemo } from 'react';

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (n, decimals = 0) =>
  n == null ? '—' : Number(n).toFixed(decimals);

const perSet = (total, matches, dec = 2) =>
  matches > 0 ? fmt(total / matches, dec) : '—';

const perSetNum = (total, matches) =>
  matches > 0 ? total / matches : null;

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return null; }
};

// ── Player level system ───────────────────────────────────────────────────────
const LEVELS = [
  { min: 0,  max: 4,  label: 'ROOKIE',   color: '#94a3b8', next: 5  },
  { min: 5,  max: 14, label: 'AMATEUR',  color: '#4ade80', next: 15 },
  { min: 15, max: 29, label: 'PRO',      color: '#38bdf8', next: 30 },
  { min: 30, max: 49, label: 'ELITE',    color: '#a78bfa', next: 50 },
  { min: 50, max: Infinity, label: 'LEGEND', color: '#fbbf24', next: null },
];

const getLevel = (matches) => {
  const lvl = LEVELS.find((l) => matches >= l.min && matches <= l.max) || LEVELS[0];
  const progress = lvl.next
    ? Math.min(100, ((matches - lvl.min) / (lvl.next - lvl.min)) * 100)
    : 100;
  return { ...lvl, progress };
};

// ── Enhanced donut chart ──────────────────────────────────────────────────────
const DonutChart = ({ kills = 0, errors = 0, attacks = 0 }) => {
  const total    = Math.max(attacks, kills + errors, 1);
  const killPct  = kills  / total;
  const errorPct = errors / total;
  const r        = 44;
  const cx = 56; const cy = 56;
  const circ     = 2 * Math.PI * r;
  const killLen  = circ * killPct;
  const errLen   = circ * errorPct;
  const hitPct   = attacks > 0 ? (kills - errors) / attacks : 0;
  const displayPct = (hitPct * 100).toFixed(3).replace(/^-/, '');
  const pctNum   = parseFloat(displayPct);
  const ringColor = pctNum >= 30 ? '#f59e0b' : pctNum >= 15 ? '#38bdf8' : '#6366f1';

  return (
    <div className="vb-donut-wrapper">
      <div className="vb-donut-ring-wrap">
        <svg viewBox="0 0 112 112" className="vb-donut-svg">
          {/* glow filter */}
          <defs>
            <filter id="ringGlow">
              <feGaussianBlur stdDeviation="3" result="blur"/>
              <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
            </filter>
          </defs>
          {/* track */}
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="13"/>
          {/* errors segment */}
          <circle cx={cx} cy={cy} r={r} fill="none"
            stroke="rgba(239,68,68,0.5)" strokeWidth="13"
            strokeDasharray={`${errLen} ${circ - errLen}`}
            strokeDashoffset={circ * 0.25 - killLen}
            strokeLinecap="round"/>
          {/* kills segment */}
          <circle cx={cx} cy={cy} r={r} fill="none"
            stroke={ringColor} strokeWidth="13"
            strokeDasharray={`${killLen} ${circ - killLen}`}
            strokeDashoffset={circ * 0.25}
            strokeLinecap="round"
            filter="url(#ringGlow)"/>
          {/* centre */}
          <text x={cx} y={cy - 5} textAnchor="middle" className="vb-donut-main">{displayPct}</text>
          <text x={cx} y={cy + 12} textAnchor="middle" className="vb-donut-sub">HIT %</text>
        </svg>
      </div>
      <div className="vb-donut-legend">
        <div className="vb-donut-row">
          <span className="vb-dot" style={{background: ringColor}}/>
          <span className="vb-donut-text">KILLS: <strong className="vb-donut-val">{kills}</strong></span>
        </div>
        <div className="vb-donut-row">
          <span className="vb-dot" style={{background:'rgba(239,68,68,0.6)'}}/>
          <span className="vb-donut-text">ERRORS: <strong className="vb-donut-val">{errors}</strong></span>
        </div>
        <div className="vb-donut-row">
          <span className="vb-dot" style={{background:'rgba(255,255,255,0.15)'}}/>
          <span className="vb-donut-text">ATTACKS: <strong className="vb-donut-val">{attacks}</strong></span>
        </div>
      </div>
    </div>
  );
};

// ── Core stat spotlight card ──────────────────────────────────────────────────
const StatSpot = ({ icon, label, value }) => (
  <div className="vb-spot">
    <div className="vb-spot__icon">{icon}</div>
    <div className="vb-spot__value">{value}</div>
    <div className="vb-spot__label">{label}</div>
  </div>
);

// ── Trophy career-high card ───────────────────────────────────────────────────
const TrophyCard = ({ icon, label, value, detail, rank }) => {
  const colors = { gold: '#fbbf24', silver: '#94a3b8', bronze: '#cd7f32' };
  const c = colors[rank] || 'rgba(255,255,255,0.2)';
  return (
    <div className="vb-trophy" style={{'--trophy-color': c}}>
      <div className="vb-trophy__icon">{icon}</div>
      <div className="vb-trophy__body">
        <div className="vb-trophy__label">{label}</div>
        <div className="vb-trophy__value">{value ?? '—'}</div>
        {detail && <div className="vb-trophy__detail">{detail}</div>}
      </div>
      {value != null && <div className="vb-trophy__badge">PB</div>}
    </div>
  );
};

// ── Per-set progress bar row ──────────────────────────────────────────────────
const BarRow = ({ label, value, rawNum, maxRef }) => {
  const pct = rawNum != null && maxRef > 0 ? Math.min(100, (rawNum / maxRef) * 100) : 0;
  return (
    <div className="vb-bar-row">
      <div className="vb-bar-header">
        <span className="vb-bar-label">{label}</span>
        <span className="vb-bar-val">{value}</span>
      </div>
      <div className="vb-bar-track">
        <div className="vb-bar-fill" style={{width: `${pct}%`}}/>
      </div>
    </div>
  );
};

// ── Recent match chip ─────────────────────────────────────────────────────────
const MatchChip = ({ match }) => {
  const kills  = match.metrics?.kill || match.metrics?.kills || 0;
  const aces   = (match.metrics?.ace || 0) + (match.metrics?.serviceAce || 0);
  const digs   = match.metrics?.dig  || match.metrics?.digs  || 0;
  const date   = formatDate(match.matchDate);
  const resultIcon = match.result === 'win' ? '🏆' : match.result === 'loss' ? '❌' : match.result === 'draw' ? '➖' : null;
  const displayTitle = match.matchTitle
    || (match.matchShortCode ? `#${match.matchShortCode}` : null)
    || `Match ${match.matchId}`;
  return (
    <div className="vb-match-chip">
      <div className="vb-match-chip__header">
        <div className="vb-match-chip__title">{displayTitle}</div>
        {resultIcon && <span className="vb-match-chip__result">{resultIcon}</span>}
      </div>
      <div className="vb-match-chip__date">{date || '—'}</div>
      <div className="vb-match-chip__stats">
        <span className="vb-chip-stat"><span className="vb-chip-ico">⚔️</span>{kills}</span>
        <span className="vb-chip-stat"><span className="vb-chip-ico">⚡</span>{aces}</span>
        <span className="vb-chip-stat"><span className="vb-chip-ico">🛡️</span>{digs}</span>
        {match.scoreContribution > 0 && (
          <span className="vb-chip-stat vb-chip-pts"><span className="vb-chip-ico">📊</span>{match.scoreContribution}</span>
        )}
      </div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const VolleyballStatsCard = ({ stats = {}, user = {}, sportProfile = {} }) => {
  const {
    careerTotals  = {},
    careerHighs   = {},
    matchesPlayed  = 0,
    winsCount      = 0,
    lossesCount    = 0,
    drawsCount     = 0,
    scoreContribution = 0,
    assists        = 0,
    recentMatches  = [],
  } = stats;

  const kills          = careerTotals.kill          || 0;
  const aces           = (careerTotals.ace || 0) + (careerTotals.serviceAce || 0);
  const serviceErrors  = careerTotals.serviceError   || 0;
  const digs           = careerTotals.dig            || 0;
  const assistsTotal   = careerTotals.assist         || assists || 0;
  const blockSolos     = careerTotals.blockSolo      || 0;
  const blockAssists   = careerTotals.blockAssist    || 0;
  const blocks         = blockSolos + blockAssists;
  const attacks        = careerTotals.attackAttempt  || Math.max(kills * 3, kills);
  const errors         = serviceErrors + (careerTotals.foul || 0);
  const tips           = careerTotals.tip            || 0;
  const setterDumps    = careerTotals.setterDump     || 0;
  const placementShots = careerTotals.placementShot  || 0;
  const hasTechniques  = tips > 0 || setterDumps > 0 || placementShots > 0;

  const level = getLevel(matchesPlayed);

  // per-set numeric values for bar scaling
  const kps  = perSetNum(kills,          matchesPlayed);
  const aps  = perSetNum(aces,           matchesPlayed);
  const dps  = perSetNum(digs,           matchesPlayed);
  const asSps= perSetNum(assistsTotal,   matchesPlayed);
  const bps  = perSetNum(blocks,         matchesPlayed);
  const tps  = perSetNum(tips,           matchesPlayed);
  const sdps = perSetNum(setterDumps,    matchesPlayed);
  const psps = perSetNum(placementShots, matchesPlayed);
  const maxBar = Math.max(kps || 0, aps || 0, dps || 0, asSps || 0, bps || 0, tps || 0, sdps || 0, psps || 0, 1);

  const high = (key) => {
    const h = careerHighs[key];
    if (!h) return { value: null, detail: null };
    return {
      value:  h.value,
      detail: h.matchDate ? `vs. ${h.matchTitle || 'match'} · ${formatDate(h.matchDate)}` : null,
    };
  };

  const aceHigh = useMemo(() => {
    const a = careerHighs.ace        || { value: 0 };
    const b = careerHighs.serviceAce || { value: 0 };
    return (a.value || 0) >= (b.value || 0) ? (careerHighs.ace || null) : (careerHighs.serviceAce || null);
  }, [careerHighs]);

  const displayName = user?.first_name
    ? `${user.first_name} ${user.last_name || ''}`.trim().toUpperCase()
    : (user?.username || 'PLAYER').toUpperCase();

  const hasStats = matchesPlayed > 0;

  return (
    <div className="vb-card">

      {/* ── HERO HEADER ─────────────────────────────────────────────────── */}
      <div className="vb-hero">
        <div className="vb-hero__bg-orbs" aria-hidden="true">
          <span className="vb-orb vb-orb--1"/>
          <span className="vb-orb vb-orb--2"/>
        </div>
        <div className="vb-hero__ball" aria-hidden="true">🏐</div>
        <div className="vb-hero__info">
          <div className="vb-hero__name">{displayName}</div>
          <div className="vb-hero__sport">Volleyball · Career Statistics</div>
          {(sportProfile?.position || sportProfile?.playingStyle) && (
            <div className="vb-hero__profile">
              {sportProfile.position     && <span className="vb-hero__position">📍 {sportProfile.position}</span>}
              {sportProfile.playingStyle && <span className="vb-hero__style">🎮 {sportProfile.playingStyle}</span>}
            </div>
          )}
        </div>
        <div className="vb-hero__meta">
          {/* Level badge */}
          <div className="vb-level-badge" style={{'--lv-color': level.color}}>
            <span className="vb-level-badge__label">LEVEL</span>
            <span className="vb-level-badge__name">{level.label}</span>
          </div>
          {/* Matches count */}
          <div className="vb-hero__matches">
            <span className="vb-hero__matches-num">{matchesPlayed}</span>
            <span className="vb-hero__matches-label">{' '}Match{matchesPlayed !== 1 ? 'es' : ''} Played</span>
          </div>
          {(winsCount > 0 || lossesCount > 0) && (
            <div className="vb-hero__record">
              <span className="vb-hero__win">🏆 {winsCount}W</span>
              <span className="vb-hero__sep"> · </span>
              <span className="vb-hero__loss">❌ {lossesCount}L</span>
              {drawsCount > 0 && <><span className="vb-hero__sep"> · </span><span className="vb-hero__draw">➖ {drawsCount}D</span></>}
            </div>
          )}
        </div>
        {/* XP bar */}
        <div className="vb-xp-bar-wrap">
          <div className="vb-xp-bar-track">
            <div className="vb-xp-bar-fill" style={{width: `${level.progress}%`}}/>
          </div>
          <span className="vb-xp-label">
            {level.next ? `${matchesPlayed} / ${level.next} matches to ${LEVELS.find(l => l.min === level.next)?.label || 'MAX'}` : '🏆 MAX LEVEL'}
          </span>
        </div>
      </div>

      {/* ── CORE STAT SPOTLIGHTS ─────────────────────────────────────────── */}
      <div className="vb-spots-row">
        <StatSpot icon="⚔️" label="KILLS"   value={hasStats ? kills  : '—'}/>
        <StatSpot icon="⚡" label="ACES"    value={hasStats ? aces   : '—'}/>
        <StatSpot icon="🛡️" label="DIGS"    value={hasStats ? digs   : '—'}/>
        <StatSpot icon="🧱" label="BLOCKS"  value={hasStats ? blocks : '—'}/>
        <StatSpot icon="🎯" label="ASSISTS" value={hasStats ? assistsTotal : '—'}/>
        <StatSpot icon="🏆" label="POINTS"  value={hasStats ? scoreContribution : '—'}/>
        {hasTechniques && <StatSpot icon="💫" label="TIPS"          value={tips}/>}
        {hasTechniques && <StatSpot icon="🎩" label="SETTER DUMPS" value={setterDumps}/>}
        {hasTechniques && <StatSpot icon="🎯" label="PLACEMENTS"   value={placementShots}/>}
      </div>

      <div className="vb-card__body">
        {/* LEFT col ──────────────────────────────────────────────────────── */}
        <div className="vb-card__left">

          {/* Career highs */}
          <h4 className="vb-section-title">🥇 CAREER HIGHS</h4>
          <div className="vb-trophies">
            <TrophyCard icon="⚔️" label="KILLS"        rank="gold"   {...high('kill')}    />
            <TrophyCard icon="🛡️" label="DIGS"         rank="silver" {...high('dig')}     />
            <TrophyCard icon="🎯" label="ASSISTS"       rank="bronze" {...high('assist')}  />
            <TrophyCard icon="⚡" label="SERVICE ACES" rank="gold"
              value={aceHigh?.value ?? null}
              detail={aceHigh?.matchDate ? `vs. ${aceHigh.matchTitle || 'match'} · ${formatDate(aceHigh.matchDate)}` : null}
            />
            <TrophyCard icon="🧱" label="BLOCK SOLOS"  rank="silver" {...high('blockSolo')}   />
            <TrophyCard icon="🪃" label="BLOCK ASSISTS" rank="bronze" {...high('blockAssist')} />
            {hasTechniques && <TrophyCard icon="💫" label="TIPS"          rank="bronze" {...high('tip')}          />}
            {hasTechniques && <TrophyCard icon="🎩" label="SETTER DUMPS" rank="silver" {...high('setterDump')}   />}
            {hasTechniques && <TrophyCard icon="🎯" label="PLACEMENTS"   rank="bronze" {...high('placementShot')}/>}
          </div>
        </div>

        {/* RIGHT col ─────────────────────────────────────────────────────── */}
        <div className="vb-card__right">

          {/* Hitting % donut */}
          <h4 className="vb-section-title">📊 HITTING PERCENTAGE</h4>
          <DonutChart kills={kills} errors={errors} attacks={attacks}/>

          {/* Per-set bars */}
          <h4 className="vb-section-title vb-section-title--mt">📈 PER-SET AVERAGES</h4>
          <div className="vb-bars">
            <BarRow label="Kills / Set"          value={perSet(kills,          matchesPlayed)} rawNum={kps}   maxRef={maxBar}/>
            <BarRow label="Aces / Set"            value={perSet(aces,           matchesPlayed)} rawNum={aps}   maxRef={maxBar}/>
            <BarRow label="Digs / Set"            value={perSet(digs,           matchesPlayed)} rawNum={dps}   maxRef={maxBar}/>
            <BarRow label="Assists / Set"         value={perSet(assistsTotal,   matchesPlayed)} rawNum={asSps} maxRef={maxBar}/>
            <BarRow label="Blocks / Set"          value={perSet(blocks,         matchesPlayed)} rawNum={bps}   maxRef={maxBar}/>
            {hasTechniques && <BarRow label="Tips / Set"             value={perSet(tips,           matchesPlayed)} rawNum={tps}   maxRef={maxBar}/>}
            {hasTechniques && <BarRow label="Setter Dumps / Set"     value={perSet(setterDumps,    matchesPlayed)} rawNum={sdps}  maxRef={maxBar}/>}
            {hasTechniques && <BarRow label="Placement Shots / Set"  value={perSet(placementShots, matchesPlayed)} rawNum={psps}  maxRef={maxBar}/>}
          </div>

          {/* Totals row */}
          <div className="vb-totals-row">
            <div className="vb-total-chip"><span className="vb-total-val">{fmt(kills)}</span><span className="vb-total-lbl">Total Kills</span></div>
            <div className="vb-total-chip"><span className="vb-total-val">{fmt(aces)}</span><span className="vb-total-lbl">Total Aces</span></div>
            <div className="vb-total-chip"><span className="vb-total-val">{fmt(scoreContribution)}</span><span className="vb-total-lbl">Points</span></div>
            {hasTechniques && <div className="vb-total-chip"><span className="vb-total-val">{fmt(tips)}</span><span className="vb-total-lbl">Tips</span></div>}
            {hasTechniques && <div className="vb-total-chip"><span className="vb-total-val">{fmt(setterDumps)}</span><span className="vb-total-lbl">Setter Dumps</span></div>}
            {hasTechniques && <div className="vb-total-chip"><span className="vb-total-val">{fmt(placementShots)}</span><span className="vb-total-lbl">Placement Shots</span></div>}
          </div>
        </div>
      </div>

      {/* ── RECENT MATCHES TIMELINE ──────────────────────────────────────── */}
      {recentMatches.length > 0 && (
        <div className="vb-recent">
          <h4 className="vb-section-title vb-section-title--center">🕹️ RECENT MATCHES</h4>
          <div className="vb-match-list">
            {recentMatches.slice(0, 5).map((m) => (
              <MatchChip key={m.matchId} match={m}/>
            ))}
          </div>
        </div>
      )}

      {/* ── EMPTY STATE ──────────────────────────────────────────────────── */}
      {!hasStats && (
        <div className="vb-empty">
          <div className="vb-empty__ball">🏐</div>
          <div className="vb-empty__heading">YOUR JOURNEY BEGINS HERE</div>
          <p className="vb-empty__sub">No advanced scoring stats recorded yet.</p>
          <p className="vb-empty__cta">
            Play a live match in <strong>Advanced Scoring</strong> mode to start
            unlocking your career statistics.
          </p>
          <div className="vb-empty__unlock-chips">
            <span className="vb-unlock-chip">⚔️ Track Kills</span>
            <span className="vb-unlock-chip">🛡️ Track Digs</span>
            <span className="vb-unlock-chip">⚡ Track Aces</span>
            <span className="vb-unlock-chip">📊 Career Highs</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default VolleyballStatsCard;
